import { useState } from "react";
import { X, CheckCircle, ArrowRight, Shield } from "lucide-react";
import { MODULE_META } from "../constants/modules";
import { TickBox, TickColumnHead, TICK_HINT } from "./NicheTickBox";

/* Canvas surface — same card shell as Settings and the Niche panel.

   flexShrink: 0 is load-bearing, not tidiness. The page is a flex column that
   scrolls, and a flex child shrinks before the container scrolls — so once the
   cards were taller than the viewport every one of them got compressed, and the
   card clips its own overflow, which ate the section rows from the bottom up.
   My Mission showed one section and half of the next. */
const card = {
  background: "var(--bg-surface)", borderRadius: 12, padding: 20,
  boxShadow: "var(--shadow-soft)", flexShrink: 0,
};
const heading = {
  margin: 0, fontSize: 13, fontWeight: 700, color: "var(--text-primary)",
  textTransform: "uppercase", letterSpacing: "0.5px",
};

/**
 * What a niche change actually touches.
 *
 * Grouped by module, and that grouping is the whole design.
 *
 * It used to be two flat lists of sections, which produced sentences like
 * "11 sections need real work, 7 just a look". That is arithmetic, not a plan —
 * eleven items spread across five modules with no shape to them, and no way to
 * tell a rebuild from a read-through. Grouped, the same work reads as five
 * rebuilds and two look-overs, which is a morning rather than a mountain.
 *
 * The flat list also quietly encouraged the wrong move. Reopening one section at
 * a time drops her into the middle of a module, and Brill's guide carries on from
 * wherever it landed. So the primary action on a card is the whole module, from
 * the top, in order — and "Still true" is the escape hatch for the pieces that
 * survived, not the other way round.
 *
 * Colour carries the tier, because it is the one thing worth knowing at a glance:
 * salmon for work built around the old client, amber for work that is only
 * niche-flavoured. Sections carry their own colour and the card takes the worst
 * one, so a mixed module — My Visibility, amber apart from Content Pillars — reads
 * as salmon on the edge with one salmon dot in an otherwise amber list. Teal is
 * deliberately absent: in this app teal means confirmed, and nothing here is.
 */
export function NicheReviewPanel({
  modules = [],
  activeNiche,
  previousNiche,
  isMobile,
  onBack,
  onRedo,
  onRebuild,
}) {
  /* Three buckets, not two, because the module tier alone would overstate the ask.
     A module with one red section among three amber ones is tiered red — correctly,
     it does hold a rebuild — but counting it alongside My Mission and saying "5
     modules to rebuild" is the overwhelming arithmetic all over again. */
  const rebuilds = modules.filter((m) => m.redoCount > 0 && m.checkCount === 0);
  const mixed = modules.filter((m) => m.redoCount > 0 && m.checkCount > 0);
  const looks = modules.filter((m) => m.redoCount === 0);
  const total = modules.length;
  const summary = [
    rebuilds.length > 0 && `${rebuilds.length} ${rebuilds.length === 1 ? "module" : "modules"} to rebuild`,
    mixed.length > 0 && `${mixed.length} mostly a read-through`,
    looks.length > 0 && `${looks.length} to look over`,
  ].filter(Boolean).join(", ");

  /* One palette lookup rather than a ternary at each use site — a card has
     eleven of them and they all have to agree. */
  const tone = (tier) => (tier === "redo"
    ? { bg: "var(--danger-quiet)", line: "var(--danger-line)", text: "var(--danger-text)" }
    : { bg: "var(--warn-bg)", line: "var(--warn-line)", text: "var(--warn-text)" });

  /* Her answers, held here and written by nothing until she presses the button on
     the card. Keyed by module and section rather than kept per card, because
     moduleCard is a plain function — it renders inside this component's tree and
     can't hold state of its own.

     Unset falls back to the tier, so the common case is confirming what Brill
     already thinks: salmon starts on "redo", amber on "still true". Reading the
     default at use time rather than seeding state from props means a module
     arriving late (the outputs load resolves after first paint) gets its defaults
     without a sync effect. */
  const [marks, setMarks] = useState({});
  const markOf = (moduleKey, name, tier) =>
    marks[`${moduleKey}::${name}`] || (tier === "redo" ? "redo" : "keep");
  const setMark = (moduleKey, name, value) =>
    setMarks((prev) => ({ ...prev, [`${moduleKey}::${name}`]: value }));

  const moduleCard = ({ moduleKey, tier, stale, redoCount, checkCount }) => {
    const meta = MODULE_META[moduleKey] || {};
    const Icon = meta.icon;
    const t = tone(tier);
    const isRebuild = tier === "redo";
    const isMixed = redoCount > 0 && checkCount > 0;
    /* What she has actually marked, not what the tier guessed. */
    const keeps = stale.filter((s) => markOf(moduleKey, s.name, s.tier) === "keep").map((s) => s.name);
    const redos = stale.length - keeps.length;
    /* Named rather than counted. "1 of 4 sections" is the arithmetic she asked us
       to stop leading with; "Content Pillars is the one that changes" is the same
       fact and tells her what to expect. */
    const redoNames = stale.filter((s) => s.tier === "redo").map((s) => s.name);

    return (
      <div
        key={moduleKey}
        style={{
          ...card,
          padding: 0, overflow: "hidden",
          border: `1px solid ${t.line}`,
          borderLeft: `3px solid ${t.text}`,
        }}
      >
        {/* ── The module itself ──────────────────────────────────────────── */}
        <div style={{ padding: 18, background: t.bg }}>
          <div style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            gap: 12, flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {Icon && <Icon size={15} color={t.text} style={{ flexShrink: 0 }} />}
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
                  {meta.label || moduleKey}
                </span>
                <span style={{
                  flexShrink: 0, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em",
                  textTransform: "uppercase", color: t.text,
                  border: `1px solid ${t.line}`, borderRadius: 5, padding: "2px 6px",
                }}>
                  {isMixed ? `${redoCount} to rebuild` : isRebuild ? "Full rebuild" : "Worth a look"}
                </span>
              </div>
              <p style={{ margin: "7px 0 0", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-secondary)" }}>
                {isMixed
                  ? <>Mostly a read-through — but <strong style={{ color: "var(--text-primary)" }}>{redoNames.join(", ")}</strong> {redoNames.length === 1 ? "was" : "were"} built around who you used to serve.</>
                  : isRebuild
                    ? <>Built around who you used to serve. We'll go through it from the top — questions, mostly, and you keep whatever still holds.</>
                    : <>Niche-flavoured language rather than niche-built. A read-through, and often nothing needs changing.</>}
              </p>
            </div>

            {/* One button, and it starts at the first section she left ticked for
                redo and works down. Everything marked still true is passed over
                without a word — that was her decision and repeating it back is
                friction. */}
            <button
              onClick={() => onRebuild(moduleKey, keeps)}
              style={{
                flexShrink: 0, width: isMobile ? "100%" : "auto",
                padding: "10px 15px", borderRadius: 9, border: "none",
                background: "var(--btn-bg)", color: "var(--btn-text)",
                fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {/* Same wording as NicheTriageDialog, and for the same reason: with
                  nothing marked for redo this files them forward and closes, so
                  "Walk through it anyway" described the one thing it does not do.
                  Two surfaces answering one question — they change together. */}
              {redos === 0
                ? keeps.length === 1 ? "Keep it as it is" : `Keep all ${keeps.length} as they are`
                : "Rebuild"}
              <ArrowRight size={12} />
            </button>
          </div>
        </div>

        {/* ── The sections inside it ─────────────────────────────────────── */}
        {/* Listed under the module rather than as the main event, and answered
            rather than acted on: ticking a box records what she thinks, and the
            button above is what starts anything. The section name is still a way
            in for the one case that isn't a rebuild — she wants to look at this
            one thing on its own, right now. */}
        <div style={{ padding: "12px 18px 16px", display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{
            display: "flex", alignItems: "flex-end", gap: 6,
            paddingBottom: 2,
          }}>
            <span style={{ flex: 1, minWidth: 0 }} />
            <TickColumnHead label="Still true" />
            <TickColumnHead label="Redo" />
          </div>
          {stale.map(({ name: sectionName, tier: sectionTier }) => {
            const mark = markOf(moduleKey, sectionName, sectionTier);
            return (
            <div
              key={sectionName}
              style={{
                display: "flex", alignItems: "center",
                gap: 6, padding: "8px 0", borderBottom: "1px solid var(--border-subtle)",
              }}
            >
              <button
                onClick={() => onRedo(moduleKey, sectionName)}
                title="Open just this section with Brill now, without rebuilding the rest of the module"
                style={{
                  fontSize: 13, minWidth: 0, flex: 1, textAlign: "left",
                  display: "flex", alignItems: "center", gap: 8,
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontFamily: "inherit",
                  color: mark === "keep" ? "var(--text-muted)" : "var(--text-primary)",
                  textDecoration: mark === "keep" ? "line-through" : "none",
                }}
              >
                {/* Its own colour, not the module's. In a mixed module that's the
                    whole point — she has to be able to see which one it is. */}
                <span style={{
                  flexShrink: 0, width: 6, height: 6, borderRadius: "50%",
                  background: tone(sectionTier).text,
                }} />
                {sectionName}
              </button>
              <TickBox
                on={mark === "keep"}
                tone="keep"
                label={`${sectionName} — still true`}
                hint={TICK_HINT.keep}
                onClick={() => setMark(moduleKey, sectionName, "keep")}
              />
              <TickBox
                on={mark === "redo"}
                tone="redo"
                label={`${sectionName} — redo`}
                hint={TICK_HINT.redo}
                onClick={() => setMark(moduleKey, sectionName, "redo")}
              />
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      flex: 1, overflowY: "auto", background: "var(--bg-primary)",
      padding: isMobile ? "20px 16px 100px" : "32px 32px 40px",
      display: "flex", flexDirection: "column", gap: 20,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>
            You changed your niche
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {previousNiche?.label
              ? <>From <strong style={{ color: "var(--text-primary)" }}>{previousNiche.label}</strong> to <strong style={{ color: "var(--text-primary)" }}>{activeNiche?.label}</strong>. Here's what that touches — and what it doesn't.</>
              : <>Now building on <strong style={{ color: "var(--text-primary)" }}>{activeNiche?.label}</strong>. Here's what that touches — and what it doesn't.</>}
          </p>
        </div>
        <button onClick={() => onBack()} style={{
          background: "var(--btn-bg)", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
          fontSize: 13, color: "var(--btn-text)", fontWeight: 600, flexShrink: 0,
        }}>
          <X size={13} />
          Close
        </button>
      </div>

      {/* The honest expectation set. Above the list on purpose — the list can't
          imply everything is a light touch if the cost is named first. */}
      {total > 0 && (
        <div style={{
          padding: "14px 16px", borderRadius: 12, flexShrink: 0,
          background: "var(--bg-surface)", border: "1px solid var(--border-subtle)",
        }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.75, color: "var(--text-secondary)" }}>
            <strong style={{ color: "var(--text-primary)" }}>{summary}.</strong>{" "}
            Most of what you've built is about <strong style={{ color: "var(--text-primary)" }}>you</strong> and still
            stands. A rebuild is a handful of questions, not a blank page — Brill starts at the top of the module, keeps
            what still holds, and anything that survived the change you can wave through in a click.
            {modules.some((m) => m.moduleKey === "market") && (
              <> My Market means new conversations with real people, so plan for that one.</>
            )}
          </p>
        </div>
      )}

      {total === 0 && (
        <div style={{ ...card, display: "flex", gap: 9, alignItems: "flex-start" }}>
          <CheckCircle size={15} color="var(--accent)" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7, color: "var(--text-secondary)" }}>
            Nothing you've locked was built on a different niche. When you reach the rest of the build, it'll come from
            your new niche automatically — same as first time through.
          </p>
        </div>
      )}

      {/* ── The affected modules, rebuilds first ──────────────────────────── */}
      {[...rebuilds, ...mixed, ...looks].map(moduleCard)}

      {/* ── Untouched ─────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
          <Shield size={13} color="var(--accent)" />
          <span style={heading}>Untouched</span>
        </div>
        <p style={{ margin: "0 0 10px", fontSize: 12, lineHeight: 1.6, color: "var(--text-secondary)" }}>
          These are about you, not your client. A niche change doesn't reach them and Brill won't ask you to revisit
          them.
        </p>
        <div style={{ fontSize: 13, lineHeight: 1.9, color: "var(--text-primary)" }}>
          My Vision · My Voice &amp; Branding
        </div>
        <p style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.6, color: "var(--text-disabled)" }}>
          Your Website, Content Plan and Course aren't listed here either. They're built from the foundation above, so
          rebuild that first and regenerate them from it.
        </p>
      </div>
    </div>
  );
}
